import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  CircularProgress,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  LinearProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  ChevronRight as ChevronRightIcon,
  RestartAlt as ResetIcon,
  Send as SendIcon,
  Chat as PlanAheadIcon,
  AccessTime as ClockIcon,
  PhoneInTalk as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth as CalendarMonthIcon,
  Search as SearchIcon,
  CancelOutlined as CancelIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import leavesSvg from "assets/folders/leaves.svg";

import {
  fetchLeaveTypes,
  applyForLeave,
  fetchLeaveSummary,
  fetchMyLeaves,
  cancelExistingLeave,
} from "store/features/company/leaveManagement/applyLeave/applyLeaveActions";
import usePermissions from "hooks/usePermissions";
import {
  selectLeaveTypes,
  selectLeaveTypesLoading,
  selectApplyLoading,
  selectLeaveSummary,
  selectLeaveSummaryLoading,
  selectMyLeaves,
  selectMyLeavesLoading,
  selectCancelLoading,
} from "store/features/company/leaveManagement/applyLeave/applyLeaveSelectors";

const INITIAL_FORM = {
  leave_type_id: "",
  start_date: "",
  end_date: "",
  reason: "",
  is_half_day: "",
  half_day_session: "",
};

const ApplyLeave = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";
  const { hasPermission } = usePermissions();

  const leaveTypes = useSelector(selectLeaveTypes);
  const leaveTypesLoading = useSelector(selectLeaveTypesLoading);
  const applyLoading = useSelector(selectApplyLoading);
  const leaveSummary = useSelector(selectLeaveSummary);
  const leaveSummaryLoading = useSelector(selectLeaveSummaryLoading);
  const myLeaves = useSelector(selectMyLeaves);
  const myLeavesLoading = useSelector(selectMyLeavesLoading);
  const cancelLoading = useSelector(selectCancelLoading);

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  // Active Tab Toggle: "apply" | "my_leaves"
  const [activeTab, setActiveTab] = useState("apply");

  // Filters for My Leaves tab
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLeaveToCancel, setSelectedLeaveToCancel] = useState(null);

  useEffect(() => {
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeaveSummary());
    dispatch(fetchMyLeaves());
  }, [dispatch]);

  /* ── Input style matching the dark sleek theme ── */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      color: isDark ? "#f8fafc" : "#111827",
      "& fieldset": {
        borderColor: isDark ? "#334155" : "#e2e8f0",
      },
      "&:hover fieldset": { borderColor: "#f59e0b" },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "1px",
      },
      "& input[type='date']::-webkit-calendar-picker-indicator": {
        filter: isDark ? "invert(1)" : "none",
        opacity: 0.6,
        cursor: "pointer",
      },
    },
    "& .MuiFormHelperText-root": {
      color: isDark ? "#64748b" : "#9ca3af",
      fontSize: "0.75rem",
      marginLeft: 0,
      marginTop: "6px"
    },
  };

  /* ── Handlers ── */
  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // Half Day selected
      if (field === "is_half_day" && Number(value) === 1) {
        updated.end_date = prev.start_date;
      }

      // If start date changes while half day is selected
      if (field === "start_date" && Number(prev.is_half_day) === 1) {
        updated.end_date = value;
      }

      // Reset half_day_session if they switch back to Full Day
      if (field === "is_half_day" && Number(value) === 0) {
        updated.half_day_session = "";
      }

      return updated;
    });
  };

  const handleDateChange = (field, value) => {
    const formattedValue = value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    handleChange(field, formattedValue);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.leave_type_id) newErrors.leave_type_id = "Leave type is required";
    if (!formData.start_date) newErrors.start_date = "Start date is required";

    // Only require end_date for Full Days
    if (Number(formData.is_half_day) === 0 && !formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    if (
      Number(formData.is_half_day) === 0 &&
      formData.start_date &&
      formData.end_date &&
      formData.end_date < formData.start_date
    ) {
      newErrors.end_date = "End date cannot be before start date";
    }

    if (!formData.reason.trim()) newErrors.reason = "Reason is required";

    // Require session selection if it's a half day
    if (Number(formData.is_half_day) === 1 && !formData.half_day_session) {
      newErrors.half_day_session = "Please select Morning or Afternoon";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      leave_type_id: Number(formData.leave_type_id),
      start_date: formData.start_date,
      reason: formData.reason,
      is_half_day: Number(formData.is_half_day),
    };

    if (Number(formData.is_half_day) === 1) {
      payload.half_day_date = formData.start_date;
      payload.half_day_session = formData.half_day_session;
    } else {
      payload.end_date = formData.end_date;
    }

    const result = await dispatch(applyForLeave(payload));
    if (result?.success) {
      setFormData(INITIAL_FORM);
      setErrors({});
    }
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
  };

  const handleConfirmCancelLeave = async () => {
    if (!selectedLeaveToCancel) return;
    await dispatch(cancelExistingLeave(selectedLeaveToCancel.id));
    setSelectedLeaveToCancel(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dayjs(dateStr).format("MMM D, YYYY");
  };

  // Label Typography styling
  const labelProps = {
    sx: {
      mb: 1,
      fontSize: "0.85rem",
      fontWeight: 600,
      color: isDark ? "#f1f5f9" : "#374151",
    },
  };

  // Merging leave types with leave balances
  const balances = leaveTypes.map((type) => {
    const summaryBal = leaveSummary?.balance?.find(
      (b) => Number(b.leave_type_id) === Number(type.id)
    );

    if (summaryBal) {
      return {
        leave_type_id: type.id,
        leave_type_name: type.name,
        total_days: Number(summaryBal.total_days),
        used_days: Number(summaryBal.used_days),
        pending_days: Number(summaryBal.pending_days),
        available_days: Number(summaryBal.available_days),
        max_days_per_year: summaryBal.max_days_per_year !== undefined && summaryBal.max_days_per_year !== null
          ? Number(summaryBal.max_days_per_year)
          : Number(type.max_days_per_year),
        max_days_per_month: summaryBal.max_days_per_month !== undefined && summaryBal.max_days_per_month !== null
          ? Number(summaryBal.max_days_per_month)
          : (type.max_days_per_month !== null ? Number(type.max_days_per_month) : null),
        carried_forward: Number(summaryBal.carried_forward) || 0,
        color: type.color || "#f59e0b",
      };
    } else {
      const total = Number(type.max_days_per_year) || 0;
      return {
        leave_type_id: type.id,
        leave_type_name: type.name,
        total_days: total,
        used_days: 0,
        pending_days: 0,
        available_days: total,
        max_days_per_year: Number(type.max_days_per_year) || 0,
        max_days_per_month: type.max_days_per_month !== null ? Number(type.max_days_per_month) : null,
        carried_forward: 0,
        color: type.color || "#f59e0b",
      };
    }
  });

  // Filtered My Leaves
  const filteredMyLeaves = (myLeaves || []).filter((leave) => {
    const matchesSearch =
      (leave.leave_type_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (leave.reason || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all"
        ? true
        : (leave.status || "").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const getStatusChipProps = (status) => {
    const normalized = (status || "").toLowerCase();
    switch (normalized) {
      case "approved":
        return { label: "Approved", color: "#10b981", bg: isDark ? "rgba(16, 185, 129, 0.15)" : "#d1fae5" };
      case "pending":
        return { label: "Pending", color: "primary.main", bg: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7" };
      case "rejected":
        return { label: "Rejected", color: "#ef4444", bg: isDark ? "rgba(239, 68, 68, 0.15)" : "#fee2e2" };
      case "cancelled":
        return { label: "Cancelled", color: "#64748b", bg: isDark ? "rgba(100, 116, 139, 0.15)" : "#e2e8f0" };
      default:
        return { label: status, color: "primary.main", bg: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7" };
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >

        {/* ── Header Banner with View Toggle ── */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "16px",
            border: 1, borderColor: "divider",
            backgroundColor: isDark ? "rgba(15, 23, 42, 0.4)" : "#ffffff",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              component="img"
              src={leavesSvg}
              alt="Leave Logo"
              onError={(e) => { e.target.style.display = "none"; }}
              sx={{
                width: { xs: 56, lg: 64 },
                height: { xs: 56, lg: 64 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.75rem", md: "2.25rem" }
                }}
              >
                {activeTab === "apply" ? "Apply Leave" : "My Leaves"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.95rem" }}
              >
                {activeTab === "apply"
                  ? "Submit a leave request for approval"
                  : "View all your submitted leave requests and track status"}
              </Typography>
            </Box>
          </Stack>

          {/* Toggle Button matching Task Page styling */}
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(e, value) => value && setActiveTab(value)}
            aria-label="leave tab mode"
            sx={{
              bgcolor: "background.paper",
              borderRadius: "12px",
              padding: "4px",
              border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px !important",
                padding: "8px 24px",
                textTransform: "none",
                fontWeight: 700,
                color: "text.secondary",
                "&.Mui-selected": {
                  backgroundColor: "rgba(245, 158, 11, 0.1)",
                  color: "primary.main",
                },
              },
              gap: 0.7,
            }}
          >
            <ToggleButton value="apply">Apply Leave</ToggleButton>
            <ToggleButton value="my_leaves">My Leaves</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        {/* ── TAB 1: APPLY LEAVE ── */}
        {activeTab === "apply" && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            {/* Left Column: Leave Overview and Balances */}
            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "35%",
                },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Stack spacing={4}>

                {/* Leave Overview Mini-Cards */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: 1, borderColor: "divider",
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: isDark ? "#f8fafc" : "#1e293b",
                      mb: 0,
                      fontSize: "0.95rem"
                    }}
                  >
                    Leave Overview
                  </Typography>

                  <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                    {/* Total Card */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
                        backgroundColor: isDark ? "rgba(59, 130, 246, 0.05)" : "#f0f7ff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        textAlign: "center"
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isDark ? "rgba(59, 130, 246, 0.15)" : "#e0f2fe",
                          mb: 1.5
                        }}
                      >
                        <CalendarTodayIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                        {leaveSummary?.summary?.total_leaves || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mt: 0.5 }}>
                        Total
                      </Typography>
                    </Box>

                    {/* Pending Card */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
                        backgroundColor: isDark ? "rgba(245, 158, 11, 0.05)" : "#fffbeb",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        textAlign: "center"
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fef3c7",
                          mb: 1.5
                        }}
                      >
                        <ClockIcon sx={{ color: "primary.main", fontSize: 18 }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                        {leaveSummary?.summary?.pending || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mt: 0.5 }}>
                        Pending
                      </Typography>
                    </Box>

                    {/* Approved Card */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
                        backgroundColor: isDark ? "rgba(16, 185, 129, 0.05)" : "#ecfdf5",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        textAlign: "center"
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#d1fae5",
                          mb: 1.5
                        }}
                      >
                        <CheckCircleIcon sx={{ color: "#10b981", fontSize: 18 }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                        {leaveSummary?.summary?.approved || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mt: 0.5 }}>
                        Approved
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* Your Leave Balances Panel */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: 1, borderColor: "divider",
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CalendarMonthIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#1e293b", fontSize: "1.05rem" }}>
                      Your Leave Balances
                    </Typography>
                  </Stack>

                  {leaveSummaryLoading && leaveTypesLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                      <CircularProgress size={30} sx={{ color: "primary.main" }} />
                    </Box>
                  ) : balances.length === 0 ? (
                    <Typography variant="body2" sx={{ color: isDark ? "#64748b" : "#9ca3af", textAlign: "center" }}>
                      No active leave types found.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {balances.map((item) => {
                        const isSelected = Number(formData.leave_type_id) === Number(item.leave_type_id);
                        const progressValue = item.total_days > 0
                          ? Math.min(100, ((item.used_days + item.pending_days) / item.total_days) * 100)
                          : 0;

                        return (
                          <Box
                            key={item.leave_type_id}
                            onClick={() => handleChange("leave_type_id", item.leave_type_id)}
                            sx={{
                              p: 2,
                              borderRadius: "12px",
                              cursor: "pointer",
                              border: `1px solid ${isSelected ? "#f59e0b" : (isDark ? "#1e293b" : "#e2e8f0")
                                }`,
                              backgroundColor: isSelected
                                ? (isDark ? "rgba(245, 158, 11, 0.08)" : "#fffbeb")
                                : (isDark ? "rgba(30, 41, 59, 0.15)" : "#ffffff"),
                              boxShadow: isSelected
                                ? "0 4px 12px -2px rgba(245, 158, 11, 0.1)"
                                : "none",
                              transition: "all 0.2s ease-in-out",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              "&:hover": {
                                borderColor: isSelected ? "#f59e0b" : (isDark ? "#334155" : "#cbd5e1"),
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                                  {item.leave_type_name}
                                </Typography>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.2,
                                    borderRadius: "20px",
                                    backgroundColor: isDark ? "rgba(59, 130, 246, 0.15)" : "#e0f2fe",
                                    color: isDark ? "#38bdf8" : "#0284c7",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {item.available_days} Available
                                </Box>
                              </Stack>
                              <ChevronRightIcon sx={{ color: isDark ? "#475569" : "#cbd5e1", fontSize: 20 }} />
                            </Stack>

                            <Box sx={{ width: "100%" }}>
                              <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: isDark ? "#1e293b" : "#e2e8f0",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: item.color,
                                    borderRadius: 3,
                                  }
                                }}
                              />
                            </Box>

                            <Grid container spacing={1.5}>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Yearly Limit
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.max_days_per_year}d
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sx={{ textAlign: "center" }}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Monthly Limit
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.max_days_per_month !== null ? `${item.max_days_per_month}d` : "No Limit"}
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sx={{ textAlign: "right" }}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Carry Forward
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.carried_forward}d
                                </Typography>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Total
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.total_days}d
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sx={{ textAlign: "center" }}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Used
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.used_days}d
                                </Typography>
                              </Grid>
                              <Grid item xs={4} sx={{ textAlign: "right" }}>
                                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#9ca3af", display: "block", fontSize: "0.68rem", fontWeight: 500 }}>
                                  Pending
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#1f2937" }}>
                                  {item.pending_days}d
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: `1px solid ${isDark ? "rgba(59, 130, 246, 0.15)" : "#bae6fd"}`,
                      backgroundColor: isDark ? "rgba(59, 130, 246, 0.05)" : "#f0f9ff",
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start"
                    }}
                  >
                    <InfoIcon sx={{ color: "#3b82f6", fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? "#38bdf8" : "#0284c7", fontSize: "0.85rem" }}>
                        Note
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#0369a1", fontWeight: 500, display: "block", mt: 0.5, lineHeight: 1.4 }}>
                        Leave balances are calculated as of today. Contact HR for any discrepancies.
                      </Typography>
                    </Box>
                  </Box>

                </Paper>
              </Stack>
            </Box>

            {/* Right Column: Leave Request Form */}
            <Box
              sx={{
                width: {
                  xs: "100%",
                  md: "65%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: 1, borderColor: "divider",
                  backgroundColor: isDark ? "#0f172a" : "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    px: 3.5,
                    py: 3,
                    borderBottom: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CalendarTodayIcon sx={{ color: "#3b82f6", fontSize: 24 }} />
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.15rem",
                          color: "text.primary",
                        }}
                      >
                        Leave Request Form
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.2 }}>
                        Fill in the details below to submit your leave request
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ p: 3.5 }}>
                    <Grid container spacing={3}>

                      {/* Leave Type */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography {...labelProps}>
                          Leave Type <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          select
                          size="small"
                          required
                          value={formData.leave_type_id}
                          onChange={(e) => handleChange("leave_type_id", e.target.value)}
                          error={!!errors.leave_type_id}
                          helperText={errors.leave_type_id || ""}
                          disabled={leaveTypesLoading}
                          sx={inputSx}
                        >
                          <MenuItem value="" disabled>Select Type</MenuItem>
                          {leaveTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Day Type */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography {...labelProps}>
                          Day Type <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          select
                          required
                          size="small"
                          value={formData.is_half_day}
                          onChange={(e) => handleChange("is_half_day", e.target.value)}
                          helperText="Choose leave duration type"
                          sx={inputSx}
                        >
                          <MenuItem value="" disabled>Select Day Type</MenuItem>
                          <MenuItem value={0}>Full Day</MenuItem>
                          <MenuItem value={1}>Half Day</MenuItem>
                        </TextField>
                      </Grid>

                      {/* Start Date */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography {...labelProps}>
                          Start Date <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <DatePicker
                          value={formData.start_date ? dayjs(formData.start_date) : null}
                          onChange={(newValue) => handleDateChange("start_date", newValue)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              required: true,
                              error: !!errors.start_date,
                              helperText: errors.start_date || "",
                              sx: inputSx
                            }
                          }}
                        />
                      </Grid>

                      {/* End Date */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography {...labelProps}>
                          End Date <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <DatePicker
                          value={formData.end_date ? dayjs(formData.end_date) : null}
                          onChange={(newValue) => handleDateChange("end_date", newValue)}
                          disabled={Number(formData.is_half_day) === 1}
                          minDate={formData.start_date ? dayjs(formData.start_date) : undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              required: Number(formData.is_half_day) === 0,
                              error: !!errors.end_date,
                              helperText: Number(formData.is_half_day) === 1 ? "Auto set for half-day" : (errors.end_date || ""),
                              sx: inputSx
                            }
                          }}
                        />
                      </Grid>

                    </Grid>

                    {/* ROW 2: Reason Field & Conditional Half Day Field */}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mt: 3, width: "100%" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography {...labelProps}>
                          Reason <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={6}
                          placeholder="Enter reason for leave..."
                          value={formData.reason}
                          onChange={(e) => handleChange("reason", e.target.value)}
                          error={!!errors.reason}
                          helperText={
                            errors.reason || "Be specific to help your manager approve quickly"
                          }
                          sx={inputSx}
                        />
                      </Box>

                      {Number(formData.is_half_day) === 1 && (
                        <Box sx={{ width: { xs: "100%", md: "250px" } }}>
                          <Typography {...labelProps}>
                            Which Half? <span style={{ color: "#ef4444" }}>*</span>
                          </Typography>

                          <ToggleButtonGroup
                            value={formData.half_day_session}
                            exclusive
                            onChange={(e, newValue) => {
                              if (newValue !== null) handleChange("half_day_session", newValue);
                            }}
                            fullWidth
                            sx={{
                              backgroundColor: isDark ? "#0f172a" : "#ffffff",
                              "& .MuiToggleButton-root": {
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                py: 0.9,
                                borderColor: errors.half_day_session
                                  ? "#ef4444"
                                  : (isDark ? "#334155" : "#e2e8f0"),
                                color: "text.secondary",
                                "&.Mui-selected": {
                                  backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
                                  color: "primary.main",
                                  borderColor: "#f59e0b",
                                }
                              }
                            }}
                          >
                            <ToggleButton value="first_half">Morning</ToggleButton>
                            <ToggleButton value="second_half">Afternoon</ToggleButton>
                          </ToggleButtonGroup>

                          <Typography
                            sx={{
                              mt: "6px",
                              fontSize: "0.75rem",
                              color: errors.half_day_session ? "#ef4444" : (isDark ? "#64748b" : "#9ca3af")
                            }}
                          >
                            {errors.half_day_session || "Select Morning or Afternoon"}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Footer Actions */}
                  <Box
                    sx={{
                      px: 3.5,
                      py: 3,
                      borderTop: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      backgroundColor: isDark ? "rgba(15, 23, 42, 0.2)" : "#fcfdfe"
                    }}
                  >
                    <Button
                      onClick={handleReset}
                      disabled={applyLoading}
                      variant="outlined"
                      startIcon={<ResetIcon />}
                      sx={{
                        px: 4,
                        borderRadius: "8px",
                        py: 1,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        borderColor: isDark ? "#334155" : "#cbd5e1",
                        color: isDark ? "#cbd5e1" : "#4b5563",
                        "&:hover": {
                          borderColor: isDark ? "#475569" : "#9ca3af",
                          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb",
                        }
                      }}
                    >
                      Reset Form
                    </Button>

                    {hasPermission("apply_leave.create") && (
                      <Button
                        type="submit"
                        disabled={applyLoading}
                        variant="contained"
                        startIcon={applyLoading ? null : <SendIcon />}
                        sx={{
                          px: 4,
                          borderRadius: "8px",
                          py: 1,
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          backgroundColor: "#2563eb",
                          color: "#ffffff",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#1d4ed8",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {applyLoading ? (
                          <CircularProgress size={22} sx={{ color: "#fff" }} />
                        ) : (
                          "Submit Leave Request"
                        )}
                      </Button>
                    )}
                  </Box>
                </form>
              </Paper>
            </Box>
          </Box>
        )}

        {/* ── TAB 2: MY LEAVES ── */}
        {activeTab === "my_leaves" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            {/* Search & Status Filter Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: 1, borderColor: "divider",
                backgroundColor: isDark ? "#0f172a" : "#ffffff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                {/* Search Field */}
                <TextField
                  placeholder="Search by leave type or reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: { xs: "100%", sm: "300px" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: isDark ? "#1e293b" : "#f8fafc",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: isDark ? "#64748b" : "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Status Filter Chips */}
                <Stack direction="row" spacing={1} overflow="auto" pb={{ xs: 1, sm: 0 }}>
                  {["all", "pending", "approved", "rejected", "cancelled"].map((status) => {
                    const isSelected = statusFilter === status;
                    const count =
                      status === "all"
                        ? (myLeaves || []).length
                        : (myLeaves || []).filter(
                          (l) => (l.status || "").toLowerCase() === status
                        ).length;

                    return (
                      <Chip
                        key={status}
                        label={`${status.charAt(0).toUpperCase() + status.slice(1)} (${count})`}
                        onClick={() => setStatusFilter(status)}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? isDark
                              ? "rgba(245, 158, 11, 0.2)"
                              : "#fffbeb"
                            : isDark
                              ? "#1e293b"
                              : "#f1f5f9",
                          color: isSelected ? "#f59e0b" : isDark ? "#94a3b8" : "#64748b",
                          border: `1px solid ${isSelected ? "#f59e0b" : isDark ? "#334155" : "#e2e8f0"
                            }`,
                        }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Paper>

            {/* My Leaves Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "16px",
                backgroundColor: isDark ? "#0f172a" : "#ffffff",
                border: 1, borderColor: "divider",
                overflow: "hidden",
              }}
            >
              {myLeavesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={36} sx={{ color: "primary.main" }} />
                </Box>
              ) : filteredMyLeaves.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                  <CalendarMonthIcon sx={{ fontSize: 48, color: isDark ? "#334155" : "#cbd5e1", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                    No leave requests found
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                    {searchTerm || statusFilter !== "all"
                      ? "Try adjusting your search or filters to see results"
                      : "You haven't submitted any leave requests yet"}
                  </Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead sx={{ backgroundColor: isDark ? "rgba(15, 23, 42, 0.8)" : "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Leave Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Duration / Dates</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Total Days</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569" }}>Applied On</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: isDark ? "#cbd5e1" : "#475569", textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMyLeaves.map((leave) => {
                      const chipProps = getStatusChipProps(leave.status);
                      const isHalf = Number(leave.is_half_day) === 1;
                      const sessionLabel =
                        leave.half_day_session === "first_half"
                          ? "Morning"
                          : leave.half_day_session === "second_half"
                            ? "Afternoon"
                            : leave.half_day_session;

                      return (
                        <TableRow
                          key={leave.id}
                          sx={{
                            borderBottom: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
                            "&:hover": {
                              backgroundColor: isDark ? "rgba(30, 41, 59, 0.4)" : "#f8fafc",
                            },
                          }}
                        >
                          {/* Leave Type */}
                          <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                            {leave.leave_type_name || "Leave"}
                          </TableCell>

                          {/* Duration / Dates */}
                          <TableCell sx={{ color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500 }}>
                            {isHalf ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatDate(leave.half_day_date || leave.start_date)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  Half Day ({sessionLabel || "Half Day"})
                                </Typography>
                              </Box>
                            ) : leave.start_date === leave.end_date ? (
                              formatDate(leave.start_date)
                            ) : (
                              `${formatDate(leave.start_date)} - ${formatDate(leave.end_date)}`
                            )}
                          </TableCell>

                          {/* Total Days */}
                          <TableCell>
                            <Chip
                              label={isHalf ? "0.5 Day" : `${leave.days || 1} Day${(leave.days || 1) > 1 ? "s" : ""}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                backgroundColor: isDark ? "rgba(59, 130, 246, 0.15)" : "#e0f2fe",
                                color: isDark ? "#38bdf8" : "#0284c7",
                              }}
                            />
                          </TableCell>

                          {/* Reason */}
                          <TableCell sx={{ color: "text.secondary", maxWidth: "250px" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.85rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {leave.reason || "-"}
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Chip
                              label={chipProps.label}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                backgroundColor: chipProps.bg,
                                color: chipProps.color,
                                border: `1px solid ${chipProps.color}`,
                              }}
                            />
                          </TableCell>

                          {/* Applied On */}
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                            {dayjs(leave.created_at).format("MMM D, YYYY")}
                          </TableCell>

                          {/* Actions */}
                          <TableCell sx={{ textAlign: "right" }}>
                            {(leave.status || "").toLowerCase() === "pending" ? (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => setSelectedLeaveToCancel(leave)}
                                startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  borderRadius: "8px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  py: 0.4,
                                  px: 1.5,
                                  borderColor: isDark ? "rgba(239, 68, 68, 0.4)" : "#fca5a5",
                                  color: "#ef4444",
                                  "&:hover": {
                                    borderColor: "#ef4444",
                                    backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "#fef2f2",
                                  },
                                }}
                              >
                                Cancel Leave
                              </Button>
                            ) : (
                              <Typography variant="caption" sx={{ color: isDark ? "#475569" : "#cbd5e1" }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        )}

      </Box>

      <br />
      {/* ── Important Information Bottom Banner ── */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#1e293b" : "#d0e3f7"}`,
            backgroundColor: isDark ? "rgba(15, 23, 42, 0.4)" : "#f4f8fd",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 5, md: 5 }}
            alignItems={{ xs: "flex-start", md: "center" }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  borderColor: isDark ? "#334155" : "#d0e3f7",
                  // mx: 2,
                }}
              />
            }
          >
            {/* Banner Label */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: "220px" }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isDark ? "rgba(37, 99, 235, 0.15)" : "#e0f2fe",
                }}
              >
                <CalendarTodayIcon sx={{ color: "#2563eb", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? "#38bdf8" : "#0284c7", fontSize: "1.05rem" }}>
                  Important Information
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}>
                  All Leave requests must be <br />
                  submitted at least 48 hours in<br />advance, except in cases of emergency.
                </Typography>
              </Box>
            </Stack>

            {/* Plan Ahead */}
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
              <PlanAheadIcon sx={{ color: "#3b82f6", fontSize: 22, mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#1e293b" }}>
                  Plan Ahead
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}>
                  Apply leaves well in advance for smooth workflow
                </Typography>
              </Box>
            </Stack>

            {/* Approval Time */}
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
              <ClockIcon sx={{ color: "#3b82f6", fontSize: 22, mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#1e293b" }}>
                  Approval Time
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}>
                  Most requests are approved within 24-48 hours
                </Typography>
              </Box>
            </Stack>

            {/* Emergency */}
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
              <PhoneIcon sx={{ color: "#10b981", fontSize: 22, mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#1e293b" }}>
                  Emergency?
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}>
                  Contact your manager directly for urgent leaves
                </Typography>
              </Box>
            </Stack>

          </Stack>
        </Paper>
      </Grid>

      {/* Confirmation Dialog for Cancellation */}
      <Dialog
        open={Boolean(selectedLeaveToCancel)}
        onClose={() => setSelectedLeaveToCancel(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: isDark ? "#0f172a" : "#ffffff",
            color: "text.primary",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Leave Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            Are you sure you want to cancel your leave request for{" "}
            <strong>{selectedLeaveToCancel?.leave_type_name}</strong> starting on{" "}
            <strong>{formatDate(selectedLeaveToCancel?.start_date)}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setSelectedLeaveToCancel(null)}
            disabled={cancelLoading}
            sx={{ color: isDark ? "#cbd5e1" : "#64748b", textTransform: "none" }}
          >
            Keep Request
          </Button>
          <Button
            onClick={handleConfirmCancelLeave}
            disabled={cancelLoading}
            variant="contained"
            color="error"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
          >
            {cancelLoading ? <CircularProgress size={20} color="inherit" /> : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplyLeave;